/**
 * Sentinel used in update calls to remove a previously set field value.
 *
 * Update arguments distinguish three intents:
 * - omit the property (`undefined`) → keep the current value unchanged
 * - pass {@link CLEAR}              → remove the previously set value
 * - pass a value                    → set the field to that value
 *
 * `CLEAR` replaces the older `null` convention, which was easy to confuse with `undefined`. It is
 * an in-process sentinel only: it cannot cross a serialization boundary (e.g. the node bridge),
 * where the wire format keeps using `null`/absent and is translated to `CLEAR` before reaching this
 * layer.
 *
 * The arguments map to the protobuf convention of using google.protobuf.StringValue fields for
 * fields that can be set optionally.
 *
 * - omit the property (`undefined`) → `nil` in backend
 * - pass {@link CLEAR}              → `""` (empty string) in backend, is interpreted as clearing it
 * - pass a value                    → string of the given value
 */
export const CLEAR = Symbol('CLEAR');

/** A field that can be set to a value or cleared. Omit it entirely to keep it unchanged. */
export type Clearable<T> = T | typeof CLEAR;

/**
 * Maps a {@link Clearable} string field to the protobuf wire value:
 * `undefined` → omit the wrapper (keep), {@link CLEAR} → `''` (clear), value → the value.
 */
export function clearableToWire(value: Clearable<string> | undefined): string | undefined {
  if (value === undefined) {
    return undefined;
  }
  return value === CLEAR ? '' : value;
}
